import axios from 'axios';
import config from '../config';

const API_BASE = config.API_URL;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// ====== 请求拦截器：自动带 token ======
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ths_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ====== 响应拦截器：401 自动退出登录 ======
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ths_token');
      localStorage.removeItem('ths_user');
      window.location.hash = '#/login';
    }
    // 附上后端返回的 message（如「获取行情失败: 麦蕊行情接口返回状态码 401」），便于页面 toast 展示根因
    if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }
    return Promise.reject(error);
  }
);

// ====== 交易接口 ======
// 股票代码补零（如 "2031" → "002031"）
function padStockCode(code) {
  if (code && code.length < 6) return code.padStart(6, '0');
  return code;
}

export const tradingApi = {
  buy: (params) => {
    const body = { ...params, stock_code: padStockCode(params.stock_code) };
    if (params.price) body.price = Math.round(params.price * 100) / 100;
    return api.post('/trading/buy', body).then(r => r.data);
  },
  buyAtPriceLevel: (params) => {
    const body = { ...params, stock_code: padStockCode(params.stock_code), ask_level: params.ask_level || 2 };
    return api.post('/trading/buy-at-price-level', body).then(r => r.data);
  },
  sellAtPriceLevel: (params) => {
    const body = { ...params, stock_code: padStockCode(params.stock_code), bid_level: params.bid_level || 2 };
    return api.post('/trading/sell-at-price-level', body).then(r => r.data);
  },
  cancel: (orderId) => api.post(`/trading/cancel/${orderId}`).then(r => r.data),
  cancelTodayOrder: (orderId, clientId) => api.post('/trading/cancel-today-order', { order_id: orderId, client_id: clientId }).then(r => r.data),
  cancelTodayOrders: (orderIds) => api.post('/trading/cancel-today-orders', { order_ids: orderIds }).then(r => r.data),
  sell: (params) => {
    const body = { ...params, stock_code: padStockCode(params.stock_code) };
    if (params.price) body.price = Math.round(params.price * 100) / 100;
    return api.post('/trading/sell', body).then(r => r.data);
  },
  cancel: (orderId) => api.post(`/trading/cancel/${orderId}`).then(r => r.data),
  getOrders: (params) => api.get('/trading/orders', { params }).then(r => r.data),
  getFills: (params) => api.get('/trading/fills', { params }).then(r => r.data),
  getQuote: (code) => api.get(`/trading/quote/${code}`).then(r => r.data),
  getQuotes: (codes) => api.post('/trading/quotes', { stock_codes: codes }).then(r => r.data),
  getMaxBuy: (params) => api.get('/trading/max-buy', { params }).then(r => r.data),
  getMaxSell: (params) => api.get('/trading/max-sell', { params }).then(r => r.data),
  getLogs: (params) => api.get('/trading/logs', { params }).then(r => r.data),
  getKLine: (code, limit, scale) => api.get(`/trading/kline/${code}`, { params: { limit, scale } }).then(r => r.data),
};

// ====== 账户接口 ======
export const accountApi = {
  getAll: (params) => api.get('/accounts', { params }).then(r => r.data),
  getPositions: (id) => api.get(`/accounts/${id}/positions`).then(r => r.data),
  refresh: (id) => api.post(`/accounts/${id}/refresh`).then(r => r.data),
  create: (data) => api.post('/accounts', data).then(r => r.data),
  getAllPositions: (params) => api.get('/positions', { params }).then(r => r.data),
  getGroupedPositions: () => api.get('/positions/grouped').then(r => r.data),
  getAssetStats: () => api.get('/stats/asset').then(r => r.data),
  update: (id, data) => api.put(`/accounts/${id}`, data).then(r => r.data),
};

// 我的分组：按当前登录账号关联客户（超级用户显示所有）
export const myGroupApi = {
  getAccounts: () => api.get('/my-group/accounts').then(r => r.data),
  getPositions: () => api.get('/my-group/positions').then(r => r.data),
  getOrders: () => api.get('/my-group/orders').then(r => r.data),
  syncAll: () => api.post('/my-group/sync').then(r => r.data),
};

// ====== 自选股行情接口 ======
export const watchlistApi = {
  getAll: () => api.get('/watchlist').then(r => r.data),
  add: (stockCode, stockName) => api.post('/watchlist', { stock_code: stockCode, stock_name: stockName }).then(r => r.data),
  remove: (id) => api.delete(`/watchlist/${id}`).then(r => r.data),
  syncFromPositions: () => api.post('/watchlist/sync-from-positions').then(r => r.data),
  sync: () => api.post('/watchlist/sync').then(r => r.data),
};

// ====== 系统接口 ======
export const systemApi = {
  getStatus: () => api.get('/system/status').then(r => r.data),
  refresh: () => api.post('/system/refresh').then(r => r.data),
};

// ====== 认证接口 ======
export const authApi = {
  changePassword: (oldPassword, newPassword) =>
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }).then(r => r.data),
};

// ====== 客户端接口 ======
export const clientApi = {
  getOnline: () => api.get('/clients').then(r => r.data),
  rename: (clientId, data) => api.post(`/clients/${clientId}/rename`, data).then(r => r.data),
  syncAccount: (clientId) => api.post(`/clients/${clientId}/sync-account`).then(r => r.data),
  syncPositions: (clientId) => api.post(`/clients/${clientId}/sync-positions`).then(r => r.data),
  syncCurrentPositions: (clientId) => api.post(`/clients/${clientId}/sync-current-positions`).then(r => r.data),
  syncFills: (clientId) => api.post(`/clients/${clientId}/sync-fills`).then(r => r.data),
  syncTodayOrders: (clientId) => api.post(`/clients/${clientId}/sync-today-orders`).then(r => r.data),
  syncSeatInfo: (clientId) => api.post(`/clients/${clientId}/sync-seat-info`).then(r => r.data),
  syncHistory: (clientId) => api.post(`/clients/${clientId}/sync-history`).then(r => r.data),
  syncDeliveries: (clientId) => api.post(`/clients/${clientId}/sync-deliveries`).then(r => r.data),
  getDeliveries: (params) => api.get('/ths/deliveries', { params }).then(r => r.data),
  syncAllClientData: (clientId) => api.post(`/clients/${clientId}/sync-all`).then(r => r.data),
  syncAll: () => api.post('/clients/sync-all').then(r => r.data),
  syncAllData: () => api.post('/clients/sync-all-data').then(r => r.data),
  syncAllTodayOrders: () => api.post('/clients/sync-all-today-orders').then(r => r.data),
  syncAllDeliveries: () => api.post('/clients/sync-all-deliveries').then(r => r.data),
  getSyncTask: (taskId) => api.get(`/clients/sync-task/${taskId}`).then(r => r.data),
  getSyncBatchRecords: () => api.get('/clients/sync-batch-records').then(r => r.data),
  getSyncBatchDetails: (batchNo) => api.get('/clients/sync-batch-details', { params: { batch_no: batchNo } }).then(r => r.data),
  triggerScreenshot: (clientId) => api.post(`/clients/${clientId}/screenshot`).then(r => r.data),
  killXiadan: (clientId) => api.post(`/clients/${clientId}/kill-xiadan`).then(r => r.data),
  startXiadan: (clientId, password) => api.post(`/clients/${clientId}/start-xiadan`, { password }).then(r => r.data),
  getTodayOrders: (clientId) => api.get('/ths/today-orders', { params: clientId ? { client_id: clientId } : {} }).then(r => r.data),
  getSeatInfo: (clientId) => api.get(`/ths/seat-info?client_id=${clientId}`).then(r => r.data),
  getScreenshots: (clientId) => api.get(`/ths/screenshots?client_id=${clientId}`).then(r => r.data),
  getLatestScreenshots: () => api.get(`/ths/screenshots/latest`).then(r => r.data),
  deleteScreenshot: (id) => api.delete(`/screenshots/${id}`).then(r => r.data),
  deleteAllScreenshots: (clientId) => api.delete('/screenshots', { params: clientId ? { client_id: clientId } : {} }).then(r => r.data),
  deleteClient: (clientId) => api.post(`/clients/${clientId}/delete`).then(r => r.data),
  closePopup: (clientId) => api.post(`/clients/${clientId}/close-popup`).then(r => r.data),
};

// ====== 客户端分组接口 ======
export const groupApi = {
  list: () => api.get('/groups').then(r => r.data),
  create: (params) => api.post('/groups', { name: params.name, client_ids: params.clientIds, parent_id: params.parent_id || '' }).then(r => r.data),
  update: (id, params) => api.put(`/groups/${id}`, { name: params.name, client_ids: params.clientIds, parent_id: params.parent_id || '' }).then(r => r.data),
  delete: (id) => api.delete(`/groups/${id}`).then(r => r.data),
  getAccounts: (id) => api.get(`/groups/${id}/accounts`).then(r => r.data),
  getPositions: (id) => api.get(`/groups/${id}/positions`).then(r => r.data),
  getOrders: (id) => api.get(`/groups/${id}/orders`).then(r => r.data),
  syncAll: (id) => api.post(`/groups/${id}/sync-all`).then(r => r.data),
};

// ====== 股票池（全量股票行情）接口 ======
export const stockPoolApi = {
  getPool: (params) => api.get('/stocks/pool', { params }).then(r => r.data),
  getIndustries: (params) => api.get('/stocks/industries', { params }).then(r => r.data),
  syncAll: () => api.post('/stocks/sync/all').then(r => r.data),
  syncMarket: (market) => api.post(`/stocks/sync/${market}`).then(r => r.data),
};

// ====== 菜单接口 ======
export const menuApi = {
  getMenus: () => api.get('/menus').then(r => r.data),
  listAll: () => api.get('/admin/menus').then(r => r.data),
  create: (data) => api.post('/admin/menus', data).then(r => r.data),
  update: (id, data) => api.put(`/admin/menus/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/admin/menus/${id}`).then(r => r.data),
};

// ====== 角色管理接口 ======
export const roleApi = {
  list: () => api.get('/admin/roles').then(r => r.data),
  create: (data) => api.post('/admin/roles', data).then(r => r.data),
  update: (id, data) => api.put(`/admin/roles/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/admin/roles/${id}`).then(r => r.data),
};

// ====== 用户管理接口 ======
export const adminUserApi = {
  list: () => api.get('/admin/users').then(r => r.data),
  create: (data) => api.post('/admin/users', data).then(r => r.data),
  update: (id, data) => api.put(`/admin/users/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/admin/users/${id}`).then(r => r.data),
};

// ====== 批次接口（统一：direction=buy 买入 / direction=sell 卖出） ======
export const batchApi = {
  create: (params) => api.post('/batches', params, { timeout: 60000 }).then(r => r.data),
  execute: (id) => api.post(`/batches/${id}/execute`).then(r => r.data),
  list: (direction) => api.get('/batches', { params: { direction } }).then(r => r.data),
  getRecords: (id) => api.get(`/batches/${id}/records`).then(r => r.data),
};

// ====== 卖出止盈任务 ======
export const sellProfitApi = {
  create: (params) => api.post('/sell-profit-tasks', params).then(r => r.data),
  list: () => api.get('/sell-profit-tasks').then(r => r.data),
  getDetails: (id) => api.get(`/sell-profit-tasks/${id}/details`).then(r => r.data),
  pause: (id) => api.post(`/sell-profit-tasks/${id}/pause`).then(r => r.data),
  start: (id) => api.post(`/sell-profit-tasks/${id}/start`).then(r => r.data),
  stop: (id) => api.post(`/sell-profit-tasks/${id}/stop`).then(r => r.data),
  remove: (id) => api.delete(`/sell-profit-tasks/${id}`).then(r => r.data),
};

// ====== 自动交易任务 ======
export const autoTradeApi = {
  create: (params) => api.post('/auto-trade-tasks', params, { timeout: 60000 }).then(r => r.data),
  list: () => api.get('/auto-trade-tasks').then(r => r.data),
  previewClients: (params) => api.get('/auto-trade-tasks/preview-clients', { params }).then(r => r.data),
  getDetails: (taskId) => api.get('/auto-trade-tasks/details', { params: { task_id: taskId } }).then(r => r.data),
  setStatus: (taskId, action) => api.post('/auto-trade-tasks/status', null, { params: { task_id: taskId, action } }).then(r => r.data),
  delete: (taskId) => api.delete('/auto-trade-tasks', { params: { task_id: taskId } }).then(r => r.data),
};

// ====== AI 选股接口 ======
export const aiApi = {
  // 与大模型对话（千问，OpenAI 兼容）；message 可含 image_url 多模态
  chat: (messages) => api.post('/ai/chat', { messages, stream: false }).then(r => r.data),
  // 聊天记录持久化
  getChatHistory: () => api.get('/ai/chat-history').then(r => r.data),
  saveChat: (payload) => api.post('/ai/chat-message', payload).then(r => r.data),
  clearChat: () => api.delete('/ai/chat-history').then(r => r.data),
  // 获取初选股票（代理 114.96.69.40:8886）；传 {date} 时仅查库该日数据（默认加载今日）
  getPreliminary: (params) => api.get('/ai/preliminary-stocks', { timeout: 60000, params }).then(r => r.data),
  // 分析初选股票（选定策略）
  analyze: (payload) => api.post('/ai/analyze', payload, { timeout: 120000 }).then(r => r.data),
  // 初选股票打分（发给千问，100 分制，按分排序返回终选）
  scoreStocks: (payload) => api.post('/ai/score-stocks', payload, { timeout: 120000 }).then(r => r.data),
  // 保存终选股票（落库，按日期+代码去重）
  saveFinal: (payload) => api.post('/ai/final-stocks', payload, { timeout: 60000 }).then(r => r.data),
  // 读取终选股票（按日期；不传 date 则今天）
  getFinal: (params) => api.get('/ai/final-stocks', { timeout: 60000, params }).then(r => r.data),
};

// ====== 通达信策略接口 ======
export const strategyApi = {
  list: () => api.get('/strategies').then(r => r.data),
  create: (data) => api.post('/strategies', data).then(r => r.data),
  update: (id, data) => api.put(`/strategies/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/strategies/${id}`).then(r => r.data),
};

export default api;
